import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Save, Tag, ShoppingBag, CreditCard, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function CommerceManager() {
  const [pricing, setPricing] = useState({ plan_price: 69, plan_currency: "€" });
  const [original, setOriginal] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data: pr } = await api.get("/admin/pricing");
      setPricing(pr);
      setOriginal(pr);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
    try {
      const { data: tx } = await api.get("/admin/payments");
      setPayments(tx);
    } catch (e) {
      // ignore
    } finally {
      setLoadingP(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!pricing.plan_price || pricing.plan_price <= 0) {
      return toast.error("Prix invalide");
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/pricing", {
        plan_price: parseInt(pricing.plan_price, 10),
        plan_currency: pricing.plan_currency || "€",
      });
      setPricing(data);
      setOriginal(data);
      toast.success("Tarif mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetDemo = async () => {
    if (!confirm("Réinitialiser la base démo MAINTENANT ?\nCela efface liens, catégories, annonces, IPs et clics, puis re-injecte un jeu de données démo.")) return;
    setResetting(true);
    try {
      await api.post("/admin/demo/reset", {});
      toast.success("Base démo réinitialisée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResetting(false);
    }
  };

  const dirty = original && (pricing.plan_price !== original.plan_price || pricing.plan_currency !== original.plan_currency);

  const STATUS_LABEL = {
    initiated: { label: "Initié", class: "text-sky-700 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300" },
    paid: { label: "Payé", class: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300" },
    failed: { label: "Échec", class: "text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300" },
    expired: { label: "Expiré", class: "text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300" },
  };

  return (
    <div className="space-y-8" data-testid="commerce-manager">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// Commercial</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Tarifs & paiements</h1>
        <p className="text-sm text-muted-foreground mt-1">Prix WiLink, historique des paiements Stripe, reset démo.</p>
      </div>

      {/* Pricing card */}
      <section className="border border-border bg-card rounded-sm p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Tag className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Prix WiLink Self-hosted</h2>
            <p className="text-sm text-muted-foreground">Affiché sur /tarifs et utilisé pour les sessions Stripe.</p>
          </div>
        </div>

        <form onSubmit={save} className="grid sm:grid-cols-[1fr_120px_auto] gap-3 items-end">
          <div>
            <Label htmlFor="plan_price">Prix (entier)</Label>
            <Input
              id="plan_price"
              type="number"
              min="1"
              step="1"
              value={pricing.plan_price}
              onChange={(e) => setPricing({ ...pricing, plan_price: parseInt(e.target.value || "0", 10) })}
              data-testid="pricing-price-input"
            />
          </div>
          <div>
            <Label htmlFor="plan_currency">Symbole</Label>
            <Input
              id="plan_currency"
              value={pricing.plan_currency}
              onChange={(e) => setPricing({ ...pricing, plan_currency: e.target.value })}
              data-testid="pricing-currency-input"
            />
          </div>
          <Button type="submit" disabled={!dirty || saving} data-testid="pricing-save">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground mt-3">
          Aperçu : <span className="font-bold text-foreground">{pricing.plan_price}{pricing.plan_currency}</span>
          {" "}— paiement unique, mises à jour à vie.
        </p>
      </section>

      {/* Payments history */}
      <section className="border border-border bg-card rounded-sm">
        <header className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-bold">Paiements Stripe</h2>
          </div>
          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{payments.length} transactions</span>
        </header>

        {loadingP ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Chargement…</p>
        ) : payments.length === 0 ? (
          <div className="p-12 text-center">
            <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">Aucun paiement enregistré.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-[0.1em] text-muted-foreground">
              <tr>
                <th className="text-left p-3 font-medium">Date</th>
                <th className="text-left p-3 font-medium">Client</th>
                <th className="text-left p-3 font-medium">Montant</th>
                <th className="text-left p-3 font-medium">Statut</th>
                <th className="text-left p-3 font-medium">Licence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payments.map((tx) => {
                const meta = STATUS_LABEL[tx.payment_status] || { label: tx.payment_status, class: "text-muted-foreground bg-muted" };
                return (
                  <tr key={tx.id} data-testid={`payment-row-${tx.id}`}>
                    <td className="p-3 text-xs">{new Date(tx.created_at).toLocaleString("fr-FR")}</td>
                    <td className="p-3">
                      <p className="font-medium">{tx.name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{tx.email || "—"}</p>
                    </td>
                    <td className="p-3 tabular-nums">{tx.amount} {tx.currency?.toUpperCase()}</td>
                    <td className="p-3">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${meta.class}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-3">
                      {tx.license_key ? (
                        <code className="font-mono text-xs">{tx.license_key}</code>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {tx.license_emailed && <p className="text-[10px] text-emerald-600">✓ email envoyé</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Demo reset */}
      <section className="border border-border bg-card rounded-sm p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="h-10 w-10 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-600 flex items-center justify-center shrink-0">
            <RotateCw className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">Base démo</h2>
            <p className="text-sm text-muted-foreground">
              Réinitialise les données publiques (liens, catégories, annonces, IPs, clics). Pour activer le reset
              automatique toutes les 30 min, ajoutez <code className="text-xs bg-muted px-1 rounded">DEMO_RESET_ENABLED=1</code> dans le <code className="text-xs bg-muted px-1 rounded">backend/.env</code>.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={resetDemo} disabled={resetting} data-testid="reset-demo-btn">
          <RotateCw className={`h-4 w-4 mr-2 ${resetting ? "animate-spin" : ""}`} />
          {resetting ? "Réinitialisation…" : "Réinitialiser maintenant"}
        </Button>
      </section>
    </div>
  );
}
